// Written by Codex on 2026-10-19
// User prompt: Separate employee accommodations, consolidate leave submission, and add dashboard Clara.
// Written by Claude Code on 2026-02-24
// User prompt: Magic patterns UI overhaul - new app structure with magic patterns layout + Firebase auth
// Updated by Claude Code on 2026-05-24
// User prompt: /app/* routing, tab-isolated sessions, lazy loading, error boundary, document titles

import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, CheckIcon, XIcon, MenuIcon } from 'lucide-react';
import { UserProvider, useUser } from './contexts/UserContext';
// Written by Codex on 2026-10-15
// User prompt: verify the AI in Certify Test Org and keep working through the ADA and FMLA demo
import ProfileRecoveryPanel from './components/ProfileRecoveryPanel';
// Written by Codex on 2026-10-15
// User prompt: fix demo failures, keep everything tested and deployed
import ErrorBoundary from './components/AppErrorBoundary';
import { NavProvider } from './contexts/NavContext';
import { ModulesProvider, useModules } from './contexts/ModulesContext';
import Login from './components/Auth/Login';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from './components/Sidebar';
import { notificationsAPI, leaveRequestsAPI } from './services/api';
// Written by Codex on 2026-10-21
// User prompt: Open the authorized case conversation directly from notifications.
import { canOpenNotificationCase, notificationCaseDestination } from './utils/notificationCaseNavigation';
// Written by Codex on 2026-10-19
// User prompt: Make HR and employee message notifications visible and refreshable.
import { filterNotificationsByRole as filterByRole } from './utils/notificationAudience';
import { ChatProvider } from './contexts/ChatContext';
import DemoWelcome from './components/DemoWelcome';
import { MODULES } from './config/modules';
import { LockIcon } from 'lucide-react';
import { Seo, organizationStructuredData } from './components/Seo';

// ─── Lazy-loaded pages ───────────────────────────────────────────────────────
// Public
const Landing             = React.lazy(() => import('./pages/Landing').then((m) => ({ default: m.Landing })));
const Pricing             = React.lazy(() => import('./pages/Pricing').then((m) => ({ default: m.Pricing })));
const DoctorForm          = React.lazy(() => import('./pages/DoctorForm').then((m) => ({ default: m.DoctorForm })));
const FmlaGuide           = React.lazy(() => import('./pages/FmlaGuide').then((m) => ({ default: m.FmlaGuide })));
const PrivacyPolicyPage   = React.lazy(() => import('./pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfServicePage  = React.lazy(() => import('./pages/TermsOfService').then((m) => ({ default: m.TermsOfService })));
const SecurityPage        = React.lazy(() => import('./pages/Security').then((m) => ({ default: m.Security })));
const PublicImpactPage    = React.lazy(() => import('./pages/PublicImpact').then((m) => ({ default: m.PublicImpact })));
const IntegrationsPage    = React.lazy(() => import('./pages/Integrations').then((m) => ({ default: m.Integrations })));
const AboutPage           = React.lazy(() => import('./pages/About').then((m) => ({ default: m.About })));
// Written by Codex on 2026-10-24
// User prompt: Push and deploy the blog and verify article links and mobile layout live.
const BlogIndexPage = React.lazy(() => import('./blog/pages/BlogIndex').then(m => ({ default: m.BlogIndex })));
const BlogArticlePage = React.lazy(() => import('./blog/pages/ArticlePage').then(m => ({ default: m.ArticlePage })));

// HR
const HRDashboardPage        = React.lazy(() => import('./pages/hr/Dashboard'));
const HRCompanySetupPage     = React.lazy(() => import('./pages/hr/CompanySetup'));
const HRAllLeavesPage        = React.lazy(() => import('./pages/hr/AllLeaves'));
const HRPendingRequestsPage  = React.lazy(() => import('./pages/hr/PendingRequests'));
const HRUserManagementPage   = React.lazy(() => import('./pages/hr/UserManagement'));
const HRComplianceReportPage = React.lazy(() => import('./pages/hr/ComplianceReport'));
const HRConnectionsPage      = React.lazy(() => import('./pages/hr/Connections'));
const HRSettingsPage         = React.lazy(() => import('./pages/hr/Settings'));
const HRAccommodationsPage   = React.lazy(() => import('./pages/hr/Accommodations'));
// Written by Codex on 2026-10-21
// User prompt: Give accommodation cases their own pages and support different screen sizes and zooms.
const HRAccommodationCasePage = React.lazy(() => import('./pages/hr/Accommodations').then(m => ({ default: m.AccommodationCasePage })));
const HRLeaveHubPage         = React.lazy(() => import('./pages/hr/LeaveHub'));
const HRLeaveCasePage        = React.lazy(() => import('./pages/hr/LeaveCase'));
const HRPeoplePage           = React.lazy(() => import('./pages/hr/People'));
const HROnboardingPage       = React.lazy(() => import('./pages/hr/Onboarding'));
const HRPayrollPage          = React.lazy(() => import('./pages/hr/Payroll'));
const HRReportsPage          = React.lazy(() => import('./pages/hr/Reports'));
const WorkflowsPage          = React.lazy(() => import('./pages/hr/Workflows'));
const HRIncidentReportsPage  = React.lazy(() => import('./pages/hr/IncidentReports'));
const HRFamliWageReportPage  = React.lazy(() => import('./pages/hr/FamliWageReport'));

// Employee
const EmployeeDashboardPage      = React.lazy(() => import('./pages/employee/Dashboard'));
const EmployeeRequestAccommodationPage = React.lazy(() => import('./pages/employee/RequestAccommodation'));
const EmployeeAccommodationCasePage = React.lazy(() => import('./pages/employee/AccommodationCase'));
const EmployeeLeaveStatusPage    = React.lazy(() => import('./pages/employee/MyLeaves'));
const EmployeeNewLeaveRequestPage = React.lazy(() => import('./pages/employee/NewLeaveRequest'));
const EmployeeCalendarPage       = React.lazy(() => import('./pages/employee/Calendar'));
const EmployeeLeavePlanningPage  = React.lazy(() => import('./pages/employee/LeavePlanning'));

// Manager
const ManagerHomePage     = React.lazy(() => import('./pages/manager/Home'));
const ManagerApprovalsPage = React.lazy(() => import('./pages/manager/Approvals'));

// Super admin
const SuperAdminDashboard = React.lazy(() => import('./pages/super/Dashboard'));

// Common
const MePage         = React.lazy(() => import('./pages/common/Me'));
const AIAssistantPage = React.lazy(() => import('./pages/common/AIAssistant'));
const PayEstimatorPage = React.lazy(() => import('./pages/common/PayEstimator'));
const RoiCalculatorPage = React.lazy(() => import('./pages/common/RoiCalculator'));
const LeakageDashboardPage = React.lazy(() => import('./pages/hr/LeakageDashboard'));

// ─── Error boundary ──────────────────────────────────────────────────────────

// ─── Page loader ─────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <div className="w-7 h-7 rounded-lg bg-teal-500 animate-pulse" />
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    </div>
  );
}

// ─── Navigation config ────────────────────────────────────────────────────────

const navByRole = {
  // HR administers leave and also takes it. 'new-leave' doubles as the intake form for
  // filing a case on someone else's behalf; the rest are the same employee-side leave tools.
  hr: ['dashboard', 'ai-assistant', 'leave-hub', 'leave-case', 'people', 'onboarding', 'all-leaves', 'pending', 'accommodations', 'incident-reports', 'users', 'compliance-report', 'workflows', 'payroll', 'new-leave', 'leave-status', 'leave-planning', 'calendar', 'pay-estimator', 'leakage', 'roi-calculator', 'reports', 'famli-wage-report', 'connections', 'settings', 'company-setup', 'profile'],
  employee: ['dashboard', 'accommodations', 'request-accommodation', 'leave-status', 'me', 'calendar', 'new-leave', 'leave-planning', 'pay-estimator', 'ai-assistant'],
  manager: ['dashboard', 'accommodations', 'request-accommodation', 'ai-assistant', 'me', 'approvals', 'leave-status', 'calendar', 'new-leave', 'leave-planning', 'pay-estimator'],
  // Written by Codex on 2026-10-19
  // User prompt: Add Clara to Superadmin for client operations.
  'super-admin': ['dashboard', 'ai-assistant', 'clients', 'user-invites', 'profile'],
};

const pageTitles = {
  dashboard: 'Dashboard',
  'leave-hub': 'Leave Hub',
  'leave-case': 'Leave Case',
  people: 'People',
  onboarding: 'Onboarding',
  payroll: 'Payroll',
  reports: 'Reports',
  me: 'Me',
  workflows: 'Workflows',
  approvals: 'Approvals',
  calendar: 'Calendar',
  'all-leaves': 'All Employee Leaves',
  pending: 'Pending Requests',
  accommodations: 'ADA / PWFA Accommodations',
  'request-accommodation': 'Request accommodation',
  users: 'User Management',
  clients: 'Clients',
  'user-invites': 'Invite Users',
  'compliance-report': 'Compliance Report',
  'leave-status': 'My Leave',
  'famli-wage-report': 'FAMLI Wage Report',
  connections: 'Connections',
  'company-setup': 'Company Setup',
  settings: 'Settings',
  'new-leave': 'New Leave Request',
  'leave-planning': 'Plan Leave',
  profile: 'Me',
  'ai-assistant': 'Clara',
  'incident-reports': 'Incident Reports',
  'pay-estimator': 'Leave Pay Planner',
  'roi-calculator': 'Leave Management ROI Calculator',
  leakage: 'Benefit Leakage',
};

// Maps nav IDs to the module that controls them (inverse of MODULES registry)
const NAV_TO_MODULE = {};
MODULES.forEach((mod) => {
  mod.navIds.forEach((navId) => { NAV_TO_MODULE[navId] = mod; });
});

function ModuleGatePage({ moduleId }) {
  const mod = MODULES.find((m) => m.id === moduleId);
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
        <LockIcon className="w-6 h-6 text-slate-400" />
      </div>
      <div className="max-w-sm">
        <h3 className="text-base font-semibold text-slate-900">{mod?.label ?? 'Feature'} not enabled</h3>
        <p className="text-sm text-slate-500 mt-1">
          This feature is not active for your organization. Contact your administrator or visit{' '}
          <strong>Settings → Features</strong> to enable it.
        </p>
      </div>
    </div>
  );
}

function GatedPage({ navId, children }) {
  const { isEnabled, effectiveIds } = useModules();
  const mod = NAV_TO_MODULE[navId];
  if (!mod) return children;
  if (effectiveIds !== null && !isEnabled(mod.id)) return <ModuleGatePage moduleId={mod.id} />;
  return children;
}

function PersonalAccommodationRoutes() {
  return <Routes><Route path="accommodations/:id" element={<EmployeeAccommodationCasePage />} /><Route path="*" element={<EmployeeLeaveStatusPage key="accommodations" accommodationsOnly />} /></Routes>;
}

function renderPage(role, nav) {
  if (role === 'hr') {
    if (nav === 'dashboard')         return <HRDashboardPage />;
    if (nav === 'people')            return <HRPeoplePage />;
    if (nav === 'connections')       return <HRConnectionsPage />;
    if (nav === 'company-setup')     return <HRCompanySetupPage />;
    if (nav === 'settings')          return <HRSettingsPage />;
    if (nav === 'users')             return <HRUserManagementPage />;
    if (nav === 'leave-hub')         return <GatedPage navId="leave-hub"><HRLeaveHubPage /></GatedPage>;
    if (nav === 'all-leaves')        return <GatedPage navId="all-leaves"><HRAllLeavesPage /></GatedPage>;
    if (nav === 'pending')           return <GatedPage navId="pending"><HRPendingRequestsPage /></GatedPage>;
    if (nav === 'leave-case')        return <GatedPage navId="leave-case"><HRLeaveCasePage /></GatedPage>;
    if (nav === 'onboarding')        return <GatedPage navId="onboarding"><HROnboardingPage /></GatedPage>;
    if (nav === 'payroll')           return <GatedPage navId="payroll"><HRPayrollPage /></GatedPage>;
    if (nav === 'reports')           return <GatedPage navId="reports"><HRReportsPage /></GatedPage>;
    if (nav === 'accommodations')    return <GatedPage navId="accommodations"><Routes><Route path="accommodations/:id" element={<HRAccommodationCasePage />} /><Route path="*" element={<HRAccommodationsPage />} /></Routes></GatedPage>;
    if (nav === 'compliance-report') return <GatedPage navId="compliance-report"><HRComplianceReportPage /></GatedPage>;
    if (nav === 'incident-reports')  return <GatedPage navId="incident-reports"><HRIncidentReportsPage /></GatedPage>;
    if (nav === 'famli-wage-report') return <GatedPage navId="famli-wage-report"><HRFamliWageReportPage /></GatedPage>;
    // Employee-side leave tools, reused as-is. 'new-leave' is also how HR files a case for
    // another employee; /leave-requests/mine is caller-scoped, so 'leave-status' shows the
    // HR admin's own leave rather than the org's.
    if (nav === 'new-leave')      return <EmployeeNewLeaveRequestPage />;
    if (nav === 'leave-status')   return <EmployeeLeaveStatusPage key="leave" includeAccommodations />;
    if (nav === 'leave-planning') return <EmployeeLeavePlanningPage />;
    if (nav === 'calendar')       return <EmployeeCalendarPage />;
  }
  if (role === 'employee') {
    if (nav === 'dashboard')    return <EmployeeDashboardPage />;
    if (nav === 'request-accommodation') return <EmployeeRequestAccommodationPage />;
    if (nav === 'accommodations') return <PersonalAccommodationRoutes />;
    if (nav === 'leave-status') return <EmployeeLeaveStatusPage key="leave" />;
    if (nav === 'calendar')     return <EmployeeCalendarPage />;
    if (nav === 'new-leave')    return <EmployeeNewLeaveRequestPage />;
    if (nav === 'leave-planning') return <EmployeeLeavePlanningPage />;
  }
  if (role === 'manager') {
    if (nav === 'request-accommodation') return <EmployeeRequestAccommodationPage />;
    if (nav === 'accommodations') return <PersonalAccommodationRoutes />;
    if (nav === 'dashboard') return <ManagerHomePage />;
    if (nav === 'approvals') return <ManagerApprovalsPage />;
    // Managers take leave too. /leave-requests/mine is caller-scoped regardless of role,
    // so this shows the manager's own requests, not their team's.
    if (nav === 'leave-status') return <EmployeeLeaveStatusPage key="leave" />;
    if (nav === 'calendar')  return <EmployeeCalendarPage />;
    if (nav === 'new-leave') return <EmployeeNewLeaveRequestPage />;
    if (nav === 'leave-planning') return <EmployeeLeavePlanningPage />;
  }
  if (role === 'super-admin') {
    if (nav === 'dashboard')    return <SuperAdminDashboard />;
    if (nav === 'clients')      return <HRUserManagementPage superAdminView="clients" />;
    if (nav === 'user-invites') return <HRUserManagementPage superAdminView="invites" />;
  }
  // HR and super-admin reach the same page through 'profile'; employees and managers through 'me'.
  if (nav === 'profile' || nav === 'me') return <MePage />;
  if (nav === 'ai-assistant')  return <AIAssistantPage />;
  if (nav === 'pay-estimator') return <PayEstimatorPage />;
  if (nav === 'roi-calculator') return <RoiCalculatorPage />;
  if (nav === 'leakage')       return <LeakageDashboardPage />;
  if (nav === 'workflows')     return <WorkflowsPage />;
  return null;
}

// ─── Notification config ──────────────────────────────────────────────────────

// Hidden until the panel's toggle and role-routing issues are fixed.
const SHOW_NOTIFICATIONS = false;

const typeConfig = {
  case_message_hr: { label: 'Employee message', color: 'bg-teal-100 text-teal-700' },
  accommodation_response_hr: { label: 'Accommodation response', color: 'bg-teal-100 text-teal-700' },
  case_message_employee: { label: 'HR message', color: 'bg-teal-100 text-teal-700' },
  certification_due:   { label: 'Certification Due',  color: 'bg-blue-100 text-blue-700' },
  cure_window:         { label: 'Cure Window',         color: 'bg-rose-100 text-rose-700' },
  recertification_due: { label: 'Recertification',     color: 'bg-amber-100 text-amber-700' },
  approval_notice:     { label: 'Approved',            color: 'bg-emerald-100 text-emerald-700' },
  denial_notice:       { label: 'Denied',              color: 'bg-rose-100 text-rose-700' },
  missing_docs:        { label: 'Docs Requested',      color: 'bg-amber-100 text-amber-700' },
  new_leave_request:   { label: 'New Request',         color: 'bg-teal-100 text-teal-700' },
};

// ─── Notification panel ───────────────────────────────────────────────────────

function NotificationPanel({ onClose, role, onUnreadChange }) {
  const navigate = useNavigate();
  const openingCase = useRef(false);
  const [openingCaseId, setOpeningCaseId] = useState(null);
  const [caseOpenError, setCaseOpenError] = useState('');
  async function openConversation(notification) {
    if (openingCase.current) return;
    openingCase.current = true;
    setOpeningCaseId(notification.id);
    setCaseOpenError('');
    try {
      const destination = await notificationCaseDestination(notification, role, leaveRequestsAPI);
      navigate(destination);
      onClose();
    } catch { setCaseOpenError('This case conversation could not be opened. Refresh notifications or contact HR.'); }
    finally { openingCase.current = false; setOpeningCaseId(null); }
  }
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [notificationError, setNotificationError] = useState('');
  const panelRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    if (role === 'super-admin') { setNotifications([]); setLoading(false); return; }
    try {
      setNotificationError('');
      const res = await notificationsAPI.getAll();
      const sorted = (res.data ?? []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(filterByRole(sorted, role));
    } catch { setNotificationError('Notifications could not be loaded. Please refresh.'); }
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => {
    fetchNotifs();
    window.addEventListener('focus', fetchNotifs);
    const timer = setInterval(fetchNotifs, 30000);
    return () => { window.removeEventListener('focus', fetchNotifs); clearInterval(timer); };
  }, [fetchNotifs]);

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const markRead = async (id) => {
    try {
      await notificationsAPI.update(id, true);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read_status: true } : n));
      onUnreadChange?.();
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    const unread = notifications.filter((n) => !n.read_status);
    const results = await Promise.allSettled(unread.map((n) => notificationsAPI.update(n.id, true)));
    await fetchNotifs();
    if (results.some(result => result.status === 'rejected')) setNotificationError('Some notifications could not be marked read. Please refresh and try again.');
    onUnreadChange?.();
  };

  const unreadCount = notifications.filter((n) => !n.read_status).length;

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, y: 6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl ring-1 ring-black/[0.08] overflow-hidden z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-teal-500 text-white rounded-full">{unreadCount}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
              <CheckIcon className="w-3 h-3" />
              Mark all read
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto max-h-[min(420px,calc(100dvh-10rem))]">
        {caseOpenError && <p role="alert" className="px-4 py-2 text-xs text-red-700">{caseOpenError}</p>}
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : notificationError ? (
          <div className="px-4 py-6 text-sm"><p role="alert">{notificationError}</p><button onClick={fetchNotifs} className="mt-2 text-teal-700">Refresh notifications</button></div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet</div>
        ) : (
          notifications.map((n) => {
            const cfg = typeConfig[n.type] || { label: n.type, color: 'bg-slate-100 text-slate-600' };
            const date = new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const isExpanded = expandedId === n.id;
            return (
              <div
                key={n.id}
                onClick={() => setExpandedId(isExpanded ? null : n.id)}
                className={`px-4 py-3 border-b border-slate-50 last:border-0 transition-colors cursor-pointer select-none ${n.read_status ? 'opacity-60' : 'bg-teal-50/30'} hover:bg-slate-50`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-slate-400">{date}</span>
                    {!n.read_status && (
                      <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="text-[10px] text-teal-600 hover:text-teal-700 font-medium">
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
                <p className={`text-xs font-medium text-slate-700 ${isExpanded ? '' : 'truncate'}`}>{n.subject}</p>
                <p className={`text-xs text-slate-400 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>{n.body}</p>
                {canOpenNotificationCase(n, role) && <button type="button" disabled={openingCaseId !== null} onClick={event => { event.stopPropagation(); openConversation(n); }} className="mt-2 text-xs font-semibold text-teal-700 disabled:opacity-50">{openingCaseId === n.id ? 'Opening case…' : 'Open case conversation'}</button>}
                {isExpanded && <p className="text-[10px] text-teal-500 mt-1.5">Click to collapse</p>}
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
}

// ─── Authenticated app shell ──────────────────────────────────────────────────

function AppContent() {
  const { firebaseUser, dbUser, loading, role, userError, logout, retryProfile } = useUser();
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('certify-sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('certify-sidebar-collapsed', String(next)); } catch { /* non-fatal */ }
      return next;
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Derive the current page key from /app/<key>
  const activeNav = location.pathname.replace(/^\/app\/?/, '').split('/')[0] || 'dashboard';
  const showRightRail = role === 'super-admin' && activeNav === 'clients';

  // Orgs created after the forced-onboarding feature shipped start with no `settings` row,
  // which reads as onboarding_wizard_completed: false — pre-existing clients were backfilled
  // to true so this never retroactively locks out companies already live in production.
  // Overriding the rendered page (rather than redirecting) keeps this true regardless of
  // which nav/URL the HR admin lands on, and clears itself the moment CompanySetup.jsx
  // finishes and refreshes dbUser — the wizard itself stays reachable afterward for edits.
  const needsCompanyOnboarding = role === 'hr' && dbUser?.company_onboarding_complete === false;

  const fetchUnreadCount = useCallback(async () => {
    if (!SHOW_NOTIFICATIONS || !firebaseUser || !role || role === 'super-admin') { setUnreadCount(0); return; }
    try {
      const res = await notificationsAPI.getAll({ unread_only: true });
      setUnreadCount(filterByRole(res.data ?? [], role).length);
    } catch { /* silent */ }
  }, [firebaseUser, role]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Redirect to default page if the current nav key is not valid for this role
  useEffect(() => {
    if (!role || !navByRole[role]) return;
    if (!navByRole[role].includes(activeNav)) {
      navigate('/app/' + navByRole[role][0], { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Keep browser tab title in sync with current page
  useEffect(() => {
    const title = needsCompanyOnboarding
      ? 'Company Setup'
      : role === 'super-admin'
        ? ({ dashboard: 'Platform Overview', clients: 'Clients', 'user-invites': 'Invite Users', profile: 'Me' }[activeNav] || 'Certify')
        : (pageTitles[activeNav] || 'Certify');
    document.title = `${title} — Certify`;
  }, [activeNav, role, needsCompanyOnboarding]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f7f5] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-500 animate-pulse" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (userError) {
    return <ProfileRecoveryPanel error={userError} onRetry={retryProfile} onSignOut={logout} loading={loading} />;
  }

  const pageTitle = needsCompanyOnboarding
    ? 'Company Setup'
    : role === 'super-admin'
      ? ({ dashboard: 'Platform Overview', clients: 'Clients', 'user-invites': 'Invite Users', profile: 'Me' }[activeNav] || 'Platform Overview')
      : (pageTitles[activeNav] || 'Dashboard');

  const handleNavChange = (nav) => { setMobileNavOpen(false); navigate('/app/' + nav); };

  const shell = (
    <div className="app-shell flex min-h-screen bg-[#f8f7f5]">
      <button className="mobile-navigation-toggle" aria-expanded={mobileNavOpen} aria-controls="app-sidebar" onClick={() => setMobileNavOpen(open => !open)}>
        <MenuIcon className="w-5 h-5" /> {mobileNavOpen ? 'Close navigation' : 'Open navigation'}
      </button>
      <DemoWelcome userId={firebaseUser?.uid} role={role} />
      <SidebarWithModules
        activeRole={role}
        activeNav={activeNav}
        onNavChange={handleNavChange}
        user={firebaseUser}
        dbUser={dbUser}
        onLogout={logout}
        collapsed={mobileNavOpen ? false : sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
        mobileOpen={mobileNavOpen}
      />

      <main
        className={`app-main min-w-0 flex-1 transition-[margin] duration-200 ease-in-out ${showRightRail ? 'xl:mr-[420px]' : ''}`}
        style={{ '--sidebar-offset': `${(sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH) + 20}px` }}
      >
        {activeNav !== 'ai-assistant' && (
          <header className="sticky top-0 z-20 bg-[#f8f7f5]/80 backdrop-blur-md border-b border-black/[0.04]">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4">
              <div className="min-w-0 break-words">
                <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
                <p className="text-sm text-slate-400 mt-0.5">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              {/* Pages can portal content into the header (see PageHeaderActions): a centered
                  slot (e.g. a step tracker; wraps below the title on narrow screens) and actions. */}
              <div id="page-header-center" className="order-last flex w-full min-w-0 justify-center empty:hidden xl:order-none xl:w-auto xl:flex-1" />
              <div id="page-header-actions" className="ml-auto flex flex-wrap items-center justify-end gap-2 empty:hidden" />
              {SHOW_NOTIFICATIONS && <div className="flex items-center gap-3 relative">
                <button
                  onClick={() => { setShowNotifs((v) => !v); if (!showNotifs) fetchUnreadCount(); }}
                  className="w-9 h-9 rounded-lg bg-white shadow-sm ring-1 ring-black/[0.06] flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors relative"
                  aria-label="Notifications"
                >
                  <BellIcon className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-teal-500 text-white text-[9px] font-bold rounded-full ring-2 ring-[#f8f7f5] flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <AnimatePresence>
                  {showNotifs && (
                    <NotificationPanel
                      role={role}
                      onClose={() => { setShowNotifs(false); fetchUnreadCount(); }}
                      onUnreadChange={fetchUnreadCount}
                    />
                  )}
                </AnimatePresence>
              </div>}
            </div>
          </header>
        )}

        <div className="app-page-content p-4 sm:p-6">
          <NavProvider onNavigate={handleNavChange}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${role}-${activeNav}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <Suspense fallback={<PageLoader />}>
                  {needsCompanyOnboarding ? <HRCompanySetupPage /> : renderPage(role, activeNav)}
                </Suspense>
              </motion.div>
            </AnimatePresence>
          </NavProvider>
        </div>
      </main>
    </div>
  );

  if (role === 'hr') {
    return (
      <ModulesProvider clientId={dbUser?.client_id}>
        {shell}
      </ModulesProvider>
    );
  }
  return shell;
}

function SidebarWithModules(props) {
  const { effectiveIds } = useModules();
  return <Sidebar {...props} enabledModuleIds={effectiveIds} />;
}

// ─── Public page wrappers ─────────────────────────────────────────────────────

function LandingPage() {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <Seo
        title="AI-Native FMLA & Leave Management Software | Certify"
        description="AI-native leave management for FMLA, ADA, and state programs. Certify tracks every deadline, reviews medical certifications on arrival, and sends employee notices on time."
        structuredData={organizationStructuredData}
      />
      <Landing
        onEnterApp={() => navigate('/login')}
        onShowPricing={() => navigate('/pricing')}
      />
    </Suspense>
  );
}

function PricingPage() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Seo
        title="AI-Native Leave Management Pricing | Certify"
        description="Explore Certify pricing for AI-native FMLA, ADA, and state leave management built for modern HR teams."
        path="/pricing"
      />
      <Pricing />
    </Suspense>
  );
}

function PublicImpactProgramPage() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Seo
        title="Public Impact Program — Free Leave Management for Schools & Nonprofits | Certify"
        description="Qualifying U.S. public school districts and nonprofits with up to 1,000 employees receive six months of no-cost access to Certify's FMLA and leave administration platform."
        path="/public-impact"
      />
      <PublicImpactPage />
    </Suspense>
  );
}

function IntegrationsMarketingPage() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Seo
        title="HRIS & Payroll Integrations for Leave Management | Certify"
        description="Certify connects to ADP, Workday, Rippling, BambooHR, Gusto, UKG Pro, Paychex, and more — importing your workforce and writing approved leave back to your system of record."
        path="/integrations"
      />
      <IntegrationsPage />
    </Suspense>
  );
}

function AboutUsPage() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Seo
        title="About Us — The Team Behind Certify Leave Management"
        description="Meet the founders of Certify, the AI-native leave management platform, and learn why we're making FMLA and ADA leave simpler for HR teams and employees."
        path="/about"
      />
      <AboutPage />
    </Suspense>
  );
}

// ─── Root router ──────────────────────────────────────────────────────────────

function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public marketing pages — always accessible, even when logged in */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/public-impact" element={<PublicImpactProgramPage />} />
        <Route path="/integrations" element={<IntegrationsMarketingPage />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogIndexPage /></Suspense>} />
        <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogArticlePage /></Suspense>} />
        <Route path="/login" element={<><Seo title="Sign In | Certify" description="Sign in to the Certify leave management platform." path="/login" noindex /><Login /></>} />
        <Route path="/doctor-form/:token" element={
          <Suspense fallback={<PageLoader />}><Seo title="Medical Certification Form | Certify" description="Secure medical certification form." noindex /><DoctorForm /></Suspense>
        } />
        <Route path="/fmla-guide" element={
          <Suspense fallback={<PageLoader />}><Seo title="FMLA Guide for Employees and HR | Certify" description="Learn the key steps, notices, forms, and timelines in the federal FMLA leave process." path="/fmla-guide" /><FmlaGuide /></Suspense>
        } />
        <Route path="/privacy-policy" element={
          <Suspense fallback={<PageLoader />}><Seo title="Privacy Policy | Certify" description="Learn how CertifyAI Technology, Inc. collects, uses, and protects personal information." path="/privacy-policy" /><PrivacyPolicyPage /></Suspense>
        } />
        <Route path="/terms" element={
          <Suspense fallback={<PageLoader />}><Seo title="Terms of Service | Certify" description="Review the terms governing use of the Certify HR and leave management platform." path="/terms" /><TermsOfServicePage /></Suspense>
        } />
        <Route path="/security" element={
          <Suspense fallback={<PageLoader />}><Seo title="Security at Certify" description="Learn how Certify protects HR, leave, and employee data with technical and organizational safeguards." path="/security" /><SecurityPage /></Suspense>
        } />

        {/* Authenticated app — all pages under /app/* */}
        <Route path="/app/*" element={
          <UserProvider>
            <ChatProvider>
              <Seo title="Certify Application" description="Certify leave management application." noindex />
              <AppContent />
            </ChatProvider>
          </UserProvider>
        } />

        {/* Unknown paths → landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
